import React, { useState } from 'react';

import { Button, RowButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';
import { Label } from '@eika/label';
import { Loader } from '@eika/loader';

import useCurrencies from '../../../fetchHooks/useCurrencies';
import type { Currency } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { EikaModal } from '../../EikaModal';
import { SelectList } from '../../shared/SelectList';
import TransferCurrency from './TransferCurrency';
import { defaultRecommendedCurrencies, RecommendedCurrency } from './currencyUtils';
import useFormContext from './form/useFormContext';

import './CurrencyField.scss';

interface Props {
    currency?: Currency;
    label: string;
    onChange: (value: Currency) => void;
    onTransferCurrencyChange: (value: Currency) => void;
    showKinaInfo?: boolean;
}

const CurrencyField: React.FC<Props> = ({ currency, label, onChange, onTransferCurrencyChange, showKinaInfo }) => {
    const translated = useTranslation();
    const { state } = useFormContext();
    const [showModal, setShowModal] = useState(false);
    const [showTransferCurrency, setShowTransferCurrency] = useState(false);
    const [searchString, setSearchString] = useState('');
    const { data: currencies, isLoading, error, refetch } = useCurrencies();

    const findCountryCurrency = currencies?.find((currency) => currency.currency === state.country?.currency);
    const recommendedCurrencies = findCountryCurrency?.name
        ? [
              ...defaultRecommendedCurrencies,
              { currency: findCountryCurrency.currency.toString(), name: findCountryCurrency.name },
          ]
        : defaultRecommendedCurrencies;
    const uniqueRecommendedCurrencies = recommendedCurrencies.reduce((result, currencyItem) => {
        if (!result.some((cur) => cur.currency === currencyItem.currency)) {
            result.push(currencyItem);
        }
        return result;
    }, [] as RecommendedCurrency[]);

    const filteredUniqueRecommendedCurrencies = uniqueRecommendedCurrencies.filter((currency) => {
        return (
            currency.name?.toLowerCase().includes(searchString.toLowerCase()) ||
            currency.currency.toString().toLowerCase().includes(searchString.toLowerCase())
        );
    });

    const filteredCurrenciesList = currencies?.filter((currency) => {
        return (
            currency.name?.toLowerCase().includes(searchString.toLowerCase()) ||
            currency.currency.toString().toLowerCase().includes(searchString.toLowerCase())
        );
    });

    return (
        <>
            <RowButton onClick={() => setShowModal(true)}>
                <div className="currency-field__layout">
                    <div className="currency-field__label">
                        <span className="currency-field__label">{label}</span>
                        <span className="currency-field__value">{String(currency ?? '')}</span>
                    </div>
                    <div className="currency-field__icon">
                        <Icon type={IconTypes.LITEN_PIL_HOEYRE} className="icon--20" ariaLabel="Endre" />
                    </div>
                    {showKinaInfo && (
                        <Label className="currency-field__kina-label" text={translated.crossBorderPayment.kinaInfo} />
                    )}
                </div>
            </RowButton>
            <EikaModal
                id="currencyField"
                className="currency-field"
                open={showModal}
                setOpen={setShowModal}
                title={translated.currencyField.label}
            >
                <p className="currency-field__description">{translated.currencyField.description}</p>
                <SearchInput
                    placeholder={translated.currencyField.searchPlaceholder}
                    value={searchString}
                    onChange={(event) => setSearchString(event.target.value)}
                    className="currency-field__search-input"
                />
                {!!filteredUniqueRecommendedCurrencies.length && (
                    <>
                        <div className="currency-field__list-title">{translated.currencyField.recommended}</div>
                        <SelectList>
                            {filteredUniqueRecommendedCurrencies.map((currencyItem) => (
                                <li key={currencyItem.name} className="currency-field__list-item">
                                    <RowButton
                                        className="currency-field__list-item-button"
                                        onClick={() => {
                                            onChange(currencyItem.currency);
                                            setShowModal(false);
                                            setSearchString('');
                                        }}
                                    >
                                        {`${currencyItem.currency} - ${currencyItem.name}`}
                                    </RowButton>
                                </li>
                            ))}
                        </SelectList>
                    </>
                )}
                {isLoading && <Loader className="currency-field__loader" mini />}
                {!error && !isLoading && !!filteredCurrenciesList?.length && (
                    <>
                        <div className="currency-field__list-title xtra-spacing">
                            {translated.currencyField.allCurrency}
                        </div>
                        <SelectList>
                            {filteredCurrenciesList?.map((currencyItem) => (
                                <li key={currencyItem.name} className="currency-field__list-item">
                                    <RowButton
                                        className="currency-field__list-item-button"
                                        onClick={() => {
                                            onChange(currencyItem.currency);
                                            const currencyIsInTransferCurrency =
                                                state.country?.transferCurrencies?.find(
                                                    (currency) => currency.currency === currencyItem.currency,
                                                );
                                            if (!currencyIsInTransferCurrency) {
                                                setShowTransferCurrency(true);
                                            }
                                            setShowModal(false);
                                            setSearchString('');
                                        }}
                                    >
                                        {`${currencyItem.currency} - ${currencyItem.name}`}
                                    </RowButton>
                                </li>
                            ))}
                        </SelectList>
                    </>
                )}
                {error && (
                    <ErrorCard
                        className="select-cross-border-receiver__fetch-receivers-error"
                        title={translated.errors.somethingWentWrong}
                        description={translated.currencyField.errorTransferCurrency}
                    >
                        <Button onClick={() => refetch()}>{translated.tryAgain}</Button>
                    </ErrorCard>
                )}
            </EikaModal>
            <EikaModal
                id="transfer-currency-field"
                open={showTransferCurrency}
                setOpen={setShowTransferCurrency}
                title={translated.currencyField.title}
            >
                <p className="transfer-currency__description">{translated.currencyField.transferCurrencyDescription}</p>
                <h3 className="transfer-currency__choose-currency-title">
                    {translated.currencyField.transferCurrencySubTitle}
                </h3>
                <TransferCurrency
                    onChange={onTransferCurrencyChange}
                    setOpen={setShowModal}
                    onCloseTransferModal={setShowTransferCurrency}
                />
            </EikaModal>
        </>
    );
};

export default CurrencyField;
